import {
  Box,
  Checkbox,
  Chip,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import { LeadsManagementData } from "../hooks/useLeadsManagementData";
import {
  STATUS_META,
  BRAND_COLOR,
  BRAND_MATCH_CHIP_COLOR,
  BRAND_MATCH_CHIP_BG,
  BRAND_MATCH_CHIP_BORDER,
  formatLeadDateTime,
  formatCurrency,
  isRejectableStatus,
  getScoreMeta,
  getQualifiedMeta,
} from "../constants";

interface LeadsTableProps {
  data: LeadsManagementData;
}

type SortableField =
  | "id"
  | "name"
  | "brand"
  | "status"
  | "date"
  | "leadScore"
  | "approvalLikelihood"
  | "qualifiedByBrand"
  | "incomeFromBrand";

type ColumnDef =
  | { type: "sort"; field: SortableField; label: string; align?: "right" }
  | { type: "plain"; label: string; align?: "right" };

// Header layout only — body cells below are hardcoded JSX kept in the same
// order, same convention as before this column set grew.
// IP Address stays "plain": has_duplicate_ip is a shared-vs-unique boolean
// on the server, not a per-lead value worth alphabetizing, and the cell
// itself renders more than one IP-derived thing (the address plus the
// duplicate-IP badge) — there's no single sort key that would represent it.
const COLUMNS: ColumnDef[] = [
  { type: "sort", field: "id", label: "ID" },
  { type: "sort", field: "name", label: "Full Name" },
  { type: "sort", field: "leadScore", label: "Lead Score" },
  { type: "sort", field: "approvalLikelihood", label: "Approval Lkl." },
  { type: "sort", field: "brand", label: "Brand Name" },
  { type: "sort", field: "status", label: "Report Status" },
  { type: "sort", field: "qualifiedByBrand", label: "Qualified by Brands" },
  { type: "sort", field: "incomeFromBrand", label: "Income", align: "right" },
  { type: "sort", field: "date", label: "Acquired" },
  { type: "plain", label: "IP Address" },
  { type: "plain", label: "Brand Match" },
];

function ScoreCell({ score }: { score: number | null }) {
  if (score === null) {
    return <Typography sx={{ fontSize: 13, color: "#bdbdbd" }}>—</Typography>;
  }
  const meta = getScoreMeta(score);
  return (
    <Box>
      <Chip
        label={score.toFixed(1)}
        size="small"
        sx={{ bgcolor: meta.bg, color: meta.color, fontWeight: 700, height: 20 }}
      />
      <Box sx={{ width: 64, height: 5, borderRadius: 3, bgcolor: "#f0f0f2", mt: 0.75, overflow: "hidden" }}>
        <Box sx={{ width: `${score * 10}%`, height: "100%", borderRadius: 3, bgcolor: meta.color }} />
      </Box>
    </Box>
  );
}

export default function LeadsTable({ data }: LeadsTableProps) {
  const {
    pageLeads,
    totalFiltered,
    pageStartIndex,
    perPage,
    setPerPage,
    pageSizeOptions,
    page,
    totalPages,
    allChecked,
    selected,
    sortField,
    sortDirection,
    toggleSort,
    toggleAllSelectable,
    toggleLeadSelected,
    openLeadDetail,
    goToPrevPage,
    goToNextPage,
    goToFilteredLeads,
    openBrandMatch,
  } = data;

  return (
    <Paper variant="outlined" sx={{ mt: 2.25, borderRadius: 2, overflow: "hidden" }}>
      <TableContainer sx={{ maxWidth: "100%", overflowX: "auto" }}>
        <Table size="small" sx={{ minWidth: 1620, "& .MuiTableCell-root": { px: 1.25 } }}>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  size="small"
                  checked={allChecked}
                  onChange={toggleAllSelectable}
                  sx={{ color: BRAND_COLOR, "&.Mui-checked": { color: BRAND_COLOR } }}
                />
              </TableCell>
              {COLUMNS.map((column) => (
                <TableCell key={column.label} align={column.align} sx={{ fontWeight: 700 }}>
                  {column.type === "sort" ? (
                    <TableSortLabel
                      active={sortField === column.field}
                      direction={sortField === column.field ? sortDirection : "asc"}
                      onClick={() => toggleSort(column.field)}
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : (
                    column.label
                  )}
                </TableCell>
              ))}
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {pageLeads.map((lead) => {
              const meta = STATUS_META[lead.status];
              const selectable = isRejectableStatus(lead.status);
              const qualifiedMeta = getQualifiedMeta(lead.qualifiedByBrand);
              const otherIpCount = lead.duplicateIpLeads.length;
              return (
                <TableRow key={lead.id} hover>
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={!!selected[lead.id]}
                      disabled={!selectable}
                      onChange={() => toggleLeadSelected(lead.id)}
                      data-tracker="928"
                      sx={{ color: BRAND_COLOR, "&.Mui-checked": { color: BRAND_COLOR } }}
                    />
                  </TableCell>
                  <TableCell sx={{ color: "#616161" }}>{lead.id}</TableCell>
                  <TableCell>
                    <Typography sx={{ fontSize: 14, fontWeight: 500, color: "#212121" }}>
                      {lead.name}
                    </Typography>
                    <Typography sx={{ fontSize: 12.5, color: "#9e9e9e" }}>{lead.email}</Typography>
                  </TableCell>
                  <TableCell>
                    <ScoreCell score={lead.leadScore} />
                  </TableCell>
                  <TableCell>
                    <ScoreCell score={lead.approvalLikelihood} />
                  </TableCell>
                  <TableCell sx={{ color: "#424242" }}>{lead.brand}</TableCell>
                  <TableCell>
                    <Chip
                      label={meta.label}
                      size="small"
                      sx={{ bgcolor: meta.bg, color: meta.color, fontWeight: 500 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={qualifiedMeta.label}
                      size="small"
                      sx={{ bgcolor: qualifiedMeta.bg, color: qualifiedMeta.color, fontWeight: 500 }}
                    />
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontSize: 13.5,
                      fontWeight: 500,
                      color: lead.incomeFromBrand ? "#2E7D32" : "#9e9e9e",
                    }}
                  >
                    {formatCurrency(lead.incomeFromBrand)}
                  </TableCell>
                  <TableCell sx={{ color: "#757575", fontSize: 13.5 }}>
                    {formatLeadDateTime(lead.date)}
                  </TableCell>
                  <TableCell>
                    <Typography
                      sx={{ fontSize: 12.5, color: "#616161", fontFamily: "'Roboto Mono', monospace" }}
                    >
                      {lead.ipAddress ?? "—"}
                    </Typography>
                    {otherIpCount > 0 && (() => {
                      const shownIds = lead.duplicateIpLeads.slice(0, 2).map((dup) => `#${dup.id}`).join(", ");
                      const remaining = otherIpCount - 2;
                      return (
                        <Box
                          onClick={() => lead.ipAddress && goToFilteredLeads({ search: lead.ipAddress })}
                          title={`${otherIpCount} other lead${otherIpCount === 1 ? "" : "s"} submitted from this IP`}
                          data-tracker="1036"
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.5,
                            mt: 0.25,
                            fontSize: 11.5,
                            color: BRAND_COLOR,
                            cursor: "pointer",
                          }}
                        >
                          <ContentCopyIcon sx={{ fontSize: 13 }} />
                          {otherIpCount} shared: {shownIds}
                          {remaining > 0 ? ` +${remaining}` : ""}
                        </Box>
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={<PlayCircleOutlineIcon sx={{ fontSize: 15, color: `${BRAND_MATCH_CHIP_COLOR} !important` }} />}
                      label="Check matches"
                      size="small"
                      onClick={() => openBrandMatch(lead.id)}
                      title="Run the brand match query for this lead"
                      data-tracker="1035"
                      sx={{
                        bgcolor: BRAND_MATCH_CHIP_BG,
                        color: BRAND_MATCH_CHIP_COLOR,
                        border: `1px solid ${BRAND_MATCH_CHIP_BORDER}`,
                        fontWeight: 500,
                        cursor: "pointer",
                      }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="View lead">
                      <IconButton
                        size="small"
                        onClick={() => openLeadDetail(lead.id)}
                        data-tracker="929"
                        sx={{ color: BRAND_COLOR }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {pageLeads.length === 0 && (
        <Box sx={{ p: 6, textAlign: "center", color: "#9e9e9e", fontSize: 15 }}>
          No leads match the current filters.
        </Box>
      )}

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2.75,
          px: 2.25,
          py: 1.75,
          borderTop: "1px solid #f2f2f2",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography sx={{ fontSize: 13.5, color: "#757575", lineHeight: "20px" }}>
            Rows per page:
          </Typography>
          <Select
            value={perPage}
            onChange={(e) => setPerPage(Number(e.target.value))}
            size="small"
            variant="standard"
            disableUnderline
            sx={{
              fontSize: 13.5,
              color: "#757575",
              "& .MuiSelect-select": {
                display: "flex",
                alignItems: "center",
                py: 0,
                lineHeight: "20px",
              },
            }}
          >
            {pageSizeOptions.map((option) => (
              <MenuItem key={option} value={option} sx={{ fontSize: 13.5 }}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </Box>
        <Typography sx={{ fontSize: 13.5, color: "#757575" }}>
          {totalFiltered === 0 ? 0 : pageStartIndex + 1}–{Math.min(pageStartIndex + perPage, totalFiltered)} of {totalFiltered}
        </Typography>
        <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1.25 }}>
          <IconButton size="small" onClick={goToPrevPage} sx={{ color: BRAND_COLOR }}>
            <ChevronLeftIcon />
          </IconButton>
          <Typography sx={{ fontSize: 13.5, color: "#616161" }}>
            {page} / {totalPages}
          </Typography>
          <IconButton size="small" onClick={goToNextPage} sx={{ color: BRAND_COLOR }}>
            <ChevronRightIcon />
          </IconButton>
        </Box>
      </Box>
    </Paper>
  );
}
